<script lang="ts" setup>
import Product from "@/models/Product";
import WishlistItem, {
  type ICustomerWishlistItem,
} from "@/models/CustomerWishlistItem";
import { computed, ref, watch } from "vue";
import auth from "@/services/AuthService";
import CustomerWishlist, {
  type ICustomerWishlist,
} from "@/models/CustomerWishlist";
import { notify } from "@kyvg/vue3-notification";
import CustomerWishlistItem from "@/models/CustomerWishlistItem";

enum WishlistItemResultType {
  None = 1,
  AddError = 2,
  AddSuccess = 3,
  RemoveError = 4,
  RemoveSuccess = 5,
}

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
  initWishlists: {
    type: Array<CustomerWishlist>,
    default: [],
    required: false,
  },
  initWishlistItems: {
    type: Array<CustomerWishlistItem>,
    default: undefined,
    required: false,
  },
});
const wishlistItem = ref<WishlistItem>();
const addingOrRemoving = ref(false);
const lastResultType = ref<WishlistItemResultType>();
const wishlists = ref<CustomerWishlist[]>([]);
const firstCustomerWishlist = computed(
  (): CustomerWishlist | undefined => wishlists.value.at(0) as CustomerWishlist,
);
const user = auth.getUser();
const loading = ref(false);
const loadingError = ref(false);

const getCustomerWishlists = async (initWishlists: ICustomerWishlist[]) => {
  if (initWishlists.length > 0) {
    wishlists.value = CustomerWishlist.fromArray(
      JSON.parse(JSON.stringify(initWishlists)) as ICustomerWishlist[],
    );
  } else if (user && user.id) {
    wishlists.value = await CustomerWishlist.getOrCreateByCustomer(
      user.id,
      true,
    );
  }
};

const getProductWishlistItems = async (initItems?: CustomerWishlistItem[]) => {
  if (props.product.id) {
    let items: CustomerWishlistItem[] = [];
    if (initItems) {
      items = CustomerWishlistItem.fromArray(
        JSON.parse(
          JSON.stringify(
            initItems.filter((item) => item.product?.id == props.product.id),
          ),
        ) as ICustomerWishlistItem[],
      );
    } else if (user && user.id) {
      items = await CustomerWishlistItem.getItemsByUserAndProduct(user.id, [
        props.product.id,
      ]);
    }
    wishlistItem.value = items.at(0);
  }
};

const loadWishlistData = async () => {
  loading.value = true;
  loadingError.value = false;
  try {
    await getCustomerWishlists(props.initWishlists);
    await getProductWishlistItems(props.initWishlistItems);
  } catch (err) {
    console.log(err);
    loadingError.value = true;
  }
  loading.value = false;
};

watch(
  [() => !props.initWishlistItems || props.initWishlistItems.length],
  () => {
    loadWishlistData();
  },
  {
    immediate: true,
  },
);

const notifyResult = () => {
  let title = "";
  let text = "";
  let type = "warn";
  switch (lastResultType.value) {
    case WishlistItemResultType.AddError:
      title = "Ocurrió un problema";
      text =
        "No se pudo agregar el producto de tus favoritos, recarga la página e intenta de nuevo.";
      break;
    case WishlistItemResultType.RemoveError:
      title = "Ocurrió un problema";
      text =
        "No se pudo quitar el producto de tus favoritos, recarga la página e intenta de nuevo.";
      break;
    case WishlistItemResultType.AddSuccess:
      title = "Producto agregado";
      text = "¡Producto agregado con éxito a tus favoritos!";
      type = "success";
      break;
    case WishlistItemResultType.RemoveSuccess:
      title = "Producto eliminado";
      text = "Producto eliminado de tus favoritos";
      type = "warn";
      break;
  }
  notify({
    type: type,
    title: title,
    text: text,
    duration: 3000,
    ignoreDuplicates: false,
  });
};

const addItem = async () => {
  CustomerWishlist.test();
  if (
    addingOrRemoving.value ||
    wishlistItem.value ||
    !firstCustomerWishlist.value
  ) {
    return;
  }
  addingOrRemoving.value = true;
  lastResultType.value = WishlistItemResultType.None;
  try {
    const newItem = new WishlistItem();
    newItem.product = props.product;
    newItem.wishlist = firstCustomerWishlist.value;
    await newItem.save();
    wishlistItem.value = newItem;
    lastResultType.value = WishlistItemResultType.AddSuccess;
  } catch (err) {
    console.error(err);
    lastResultType.value = WishlistItemResultType.AddError;
  } finally {
    addingOrRemoving.value = false;
    notifyResult();
  }
};

const removeItem = async () => {
  if (
    addingOrRemoving.value ||
    !wishlistItem.value ||
    !firstCustomerWishlist.value
  ) {
    return;
  }
  addingOrRemoving.value = true;
  lastResultType.value = WishlistItemResultType.None;
  try {
    await wishlistItem.value.delete();
    wishlistItem.value = undefined;
    lastResultType.value = WishlistItemResultType.RemoveSuccess;
  } catch (err) {
    console.error(err);
    lastResultType.value = WishlistItemResultType.RemoveError;
  } finally {
    addingOrRemoving.value = false;
    notifyResult();
  }
};
</script>
<template>
  <div v-if="user && user.id">
    <template v-if="firstCustomerWishlist && !loading">
      <button
        v-if="wishlistItem"
        type="button"
        class="btn btn-light"
        :disabled="addingOrRemoving"
        aria-label="Quitar de favoritos"
        @click.prevent="removeItem()"
      >
        <span
          v-if="addingOrRemoving"
          class="spinner-border spinner-border-sm"
        ></span>
        <i v-else class="bi bi-heart-fill text-danger"></i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-secondary opacity-75"
        aria-label="Agregar a favoritos"
        :disabled="addingOrRemoving"
        @click.prevent="addItem()"
      >
        <span
          v-if="addingOrRemoving"
          class="spinner-border spinner-border-sm"
        ></span>
        <i v-else class="bi bi-heart"></i>
      </button>
    </template>
    <template v-else-if="loading">
      <button
        type="button"
        class="btn btn-outline-secondary opacity-75"
        aria-label="Cargando favorito"
        :disabled="true"
        @click.prevent
      >
        <span class="spinner-border spinner-border-sm"></span>
      </button>
    </template>
    <template v-else-if="loadingError && user">
      <span class="small text-danger">Error con botón de favorito</span>
    </template>
  </div>
</template>
